import * as yup from "yup";

const today = new Date();
const minDate: Date = new Date('1850-01-01');
const POSITIVE_NAAT_VALUE = "Positive";
const DISCLOSED_VALUE = "Yes";
const POSITIVE_HIVAgAB_VALUE = "Positive";
const InCare_Yes_VALUE = "Yes";
const POSITIVE_HIVIa_VALUE = "Positive";

export interface NavItem {
  id: string;      // This must exactly match the HTML div id
  label: string;   // The text displayed on the sidebar button
}
export const DASHBOARD_SECTIONS: NavItem[] = [
  { id: 'PatientInfo', label: 'PatientInfo' },
  { id: 'PatientContactInfo', label: 'PatientContactInfo' },
  { id: 'MedicalInfo', label: 'MedicalInfo' },
  { id: 'TestInfo', label: 'TestInfo' }
];

export type providerDashboardSchema = yup.InferType<typeof TestingInformationValidation>;

export const TestingInformationValidation = yup.object().shape({
  selfReported: yup.boolean(),
  caseReportCheckBox: yup.boolean(),
  section1: yup.boolean(),
  section2: yup.boolean(),
  section3: yup.boolean(),
  naat: yup.string(),

  hivAgAb: yup.string(),

  hivIa: yup.string(),

  disclosed: yup.string(),
  CaseBirthGender: yup.string(),

  inCare: yup.string(),
  routinizedHospitalName: yup.string().required("Routinized Hospital is required"),
  routinizedFacilityName: yup.string().required("Routinized Facility is required"),
  medicalRecord: yup.string().required("Medical Record is required"),

  dateReceived: yup
    .string()
    .test(
      'date-validation',
      'Date Received is not valid',
      (value) => {
        if (!value) return true; // This field is not a required one
        const inputDate = new Date(value);
        return inputDate <= today && inputDate >= minDate;
      }
    ).required("Date Received is required"),

  firstName: yup.string().required("First Name is required"),
  lastName: yup.string().required("Last Name is required"),
  dob: yup
    .string()
    .test(
      'date-validation',
      'Date Received is not valid',
      (value) => {
        if (!value) return true; // This field is not a required one
        const inputDate = new Date(value);
        return inputDate <= today && inputDate >= minDate;
      }
    ).required("Date of Birth is required"),

  datePresented: yup
    .string()
    .test(
      'date-validation',
      'Date Presented is not valid',
      (value) => {
        if (!value) return true; // This field is not a required one
        const inputDate = new Date(value);
        return inputDate <= today && inputDate >= minDate;
      }
    ),
  dateDischarged: yup
    .string()
    .test(
      'date-validation',
      'Date Discharged is not valid',
      (value) => {
        if (!value) return true; // This field is not a required one
        const inputDate = new Date(value);
        return inputDate <= today && inputDate >= minDate;
      }
    ),




  naatDate: yup
    .string() // 1. Use .date() for proper date validation
    .nullable() // 2. Allow it to be null/empty unless required
    .when('naat', {
      // Condition: If naat equals POSITIVE_NAAT_VALUE
      is: (naatValue: string) => naatValue === POSITIVE_NAAT_VALUE,

      // THEN: Apply required and date range constraints
      then: (schema) => schema
        .required("NAAT Date is required") // 3. Make it required
        .test(
          'date-validation',
          'Naat Date is not valid',
          (value) => {
            if (!value) return true; // This field is not a required one
            const inputDate = new Date(value);
            return inputDate <= today && inputDate >= minDate;
          }
        ),

      // OTHERWISE: Do not apply any extra validation (it remains nullable)
      otherwise: (schema) => schema
    }),

  disclosedDate: yup
    .string() // 1. Use .date() for proper date validation
    .nullable() // 2. Allow it to be null/empty unless required
    .when('disclosed', {
      // Condition: If naat equals POSITIVE_NAAT_VALUE
      is: (disclosureValue: string) => disclosureValue === DISCLOSED_VALUE,

      // THEN: Apply required and date range constraints
      then: (schema) => schema
        .required("Disclosed Date is required") // 3. Make it required
        .test(
          'date-validation',
          'Disclosure Date is not valid',
          (value) => {
            if (!value) return true; // This field is not a required one
            const inputDate = new Date(value);
            return inputDate <= today && inputDate >= minDate;
          }
        ),

      // OTHERWISE: Do not apply any extra validation (it remains nullable)
      otherwise: (schema) => schema
    }),

  hivAgAbDate: yup
    .string() // 1. Use .date() for proper date validation
    .nullable() // 2. Allow it to be null/empty unless required
    .when('hivAgAb', {
      // Condition: If naat equals POSITIVE_NAAT_VALUE
      is: (hivagabValue: string) => hivagabValue === POSITIVE_HIVAgAB_VALUE,

      // THEN: Apply required and date range constraints
      then: (schema) => schema
        .required("HIV AG AB Date is required") // 3. Make it required
        .test(
          'date-validation',
          'HIV AG AB Date is not valid',
          (value) => {
            if (!value) return true; // This field is not a required one
            const inputDate = new Date(value);
            return inputDate <= today && inputDate >= minDate;
          }
        ),

      // OTHERWISE: Do not apply any extra validation (it remains nullable)
      otherwise: (schema) => schema
    }),
  inCareDate: yup
    .string() // 1. Use .date() for proper date validation
    .nullable() // 2. Allow it to be null/empty unless required
    .when('inCare', {
      // Condition: If naat equals POSITIVE_NAAT_VALUE
      is: (incareValue: string) => incareValue === InCare_Yes_VALUE,

      // THEN: Apply required and date range constraints
      then: (schema) => schema
        .required("In Care Date is required") // 3. Make it required
        .test(
          'date-validation',
          'In Care Date is not valid',
          (value) => {
            if (!value) return true; // This field is not a required one
            const inputDate = new Date(value);
            return inputDate <= today && inputDate >= minDate;
          }
        ),

      // OTHERWISE: Do not apply any extra validation (it remains nullable)
      otherwise: (schema) => schema
    }),
  hivIaSupplementalDate: yup
    .string() // 1. Use .date() for proper date validation
    .nullable() // 2. Allow it to be null/empty unless required
    .when('hivIa', {
      // Condition: If naat equals POSITIVE_NAAT_VALUE
      is: (hivIaValue: string) => hivIaValue === POSITIVE_HIVIa_VALUE,

      // THEN: Apply required and date range constraints
      then: (schema) => schema
        .required("HIV IA Date is required") // 3. Make it required
        .test(
          'date-validation',
          'HIV - 1/2 IA Date is not valid',
          (value) => {
            if (!value) return true; // This field is not a required one
            const inputDate = new Date(value);
            return inputDate <= today && inputDate >= minDate;
          }
        ),
      // OTHERWISE: Do not apply any extra validation (it remains nullable)
      otherwise: (schema) => schema
    }),

  hivDiagnosisDate: yup
    .string()
    .test(
      'date-validation',
      'HIV Diagnosis Date is not valid',
      (value) => {
        if (!value) return true; // This field is not a required one
        const inputDate = new Date(value);
        return inputDate <= today && inputDate >= minDate;
      }
    ),
  updatedHivdateofDiagnosis: yup
    .string()
    .test(
      'date-validation',
      'HIV Diagnosis Date is not valid',
      (value) => {
        if (!value) return true; // This field is not a required one
        const inputDate = new Date(value);
        return inputDate <= today && inputDate >= minDate;
      }
    ),
  aidsDate: yup
    .string()
    .test(
      'date-validation',
      'Aids Date is not valid',
      (value) => {
        if (!value) return true; // This field is not a required one
        const inputDate = new Date(value);
        return inputDate <= today && inputDate >= minDate;
      }
    ),
  updatedAidsDate: yup
    .string()
    .test(
      'date-validation',
      'Aids Date is not valid',
      (value) => {
        if (!value) return true; // This field is not a required one
        const inputDate = new Date(value);
        return inputDate <= today && inputDate >= minDate;
      }
    ),
  dateReportedHivPositive: yup
    .string()
    .test(
      'date-validation',
      'Date Reported HIV Positive is not valid',
      (value) => {
        if (!value) return true; // This field is not a required one
        const inputDate = new Date(value);
        return inputDate <= today && inputDate >= minDate;
      }
    ),
  updatedDateReportedHivPositive: yup
    .string()
    .test(
      'date-validation',
      'Date Reported HIV Positive is not valid',
      (value) => {
        if (!value) return true; // This field is not a required one
        const inputDate = new Date(value);
        return inputDate <= today && inputDate >= minDate;
      }
    ),
  lastCd4: yup.string()
    .test(
      "dob-not-in-future",
      "Date of last CD4 is not valid",
      (value) => {
        if (!value) return true; // This field is not a required one
        const inputDate = new Date(value);
        return inputDate <= today && inputDate >= minDate;
      }
    ),
  updatedlastCd4: yup.string()
    .test(
      "dob-not-in-future",
      "Date of last CD4 is not valid",
      (value) => {
        if (!value) return true; // This field is not a required one
        const inputDate = new Date(value);
        return inputDate <= today && inputDate >= minDate;
      }
    ),

  lastViralLoad: yup.string()
    .test(
      "dob-not-in-future",
      "Date of last viral load is not valid",
      (value) => {
        if (!value) return true; // This field is not a required one
        const inputDate = new Date(value);
        return inputDate <= today && inputDate >= minDate;
      }
    ),
  updatedLastViralLoad: yup.string()
    .test(
      "dob-not-in-future",
      "Date of last viral load is not valid",
      (value) => {
        if (!value) return true; // This field is not a required one
        const inputDate = new Date(value);
        return inputDate <= today && inputDate >= minDate;
      }
    ),

  showStdTestInfo: yup.string(),

  // RPR Test Information - Conditionally Required (only when STD section shown AND a type is selected)
  UpdatedRprType: yup.string(),

  UpdatedRprType1: yup.string().when(['showStdTestInfo', 'UpdatedRprType'], {
    is: (showStdTestInfo: any, val: any) => showStdTestInfo === 'true' && val === 'Other',
    then: schema => schema.required("Specify Other Test"),
    otherwise: schema => schema
  }),

  UpdatedRprSpecimenCollectionDate: yup.string().when(['showStdTestInfo', 'UpdatedRprType'], {
    is: (showStdTestInfo: any, value: any) => showStdTestInfo === 'true' && value && value !== "0" && value !== "",
    then: schema => schema
      .required("Specimen Collection date is required")
      .test('date-validation', 'RPR Date is not valid',
        value => !value || (new Date(value) <= today && new Date(value) >= minDate)),
    otherwise: schema => schema
  }),

  UpdatedRprDetails: yup.string().when(['showStdTestInfo', 'UpdatedRprType'], {
    is: (showStdTestInfo: any, value: any) => showStdTestInfo === 'true' && value && value !== "0" && value !== "",
    then: schema => schema.required("Detail is required").notOneOf([0, "0"] as any, "Detail is required"),
    otherwise: schema => schema
  }),

  // VDRL Test Fields - Conditionally Required (only when STD section shown AND a type is selected)
  UpdatedVdrlType: yup.string(),

  UpdatedVdrlType1: yup.string().when(['showStdTestInfo', 'UpdatedVdrlType'], {
    is: (showStdTestInfo: any, val: any) => showStdTestInfo === 'true' && val === 'Other',
    then: schema => schema.required("Specify Other Test"),
    otherwise: schema => schema
  }),

  UpdatedVdrlSpecimenCollectionDate: yup.string().when(['showStdTestInfo', 'UpdatedVdrlType'], {
    is: (showStdTestInfo: any, value: any) => showStdTestInfo === 'true' && value && value !== "0" && value !== "",
    then: schema => schema
      .required("Specimen Collection date is required")
      .test('date-validation', 'VDRL Date is not valid',
        value => !value || (new Date(value) <= today && new Date(value) >= minDate)),
    otherwise: schema => schema
  }),

  UpdatedVdrlDetails: yup.string().when(['showStdTestInfo', 'UpdatedVdrlType'], {
    is: (showStdTestInfo: any, value: any) => showStdTestInfo === 'true' && value && value !== "0" && value !== "",
    then: schema => schema.required("Detail is required").notOneOf([0, "0"] as any, "Detail is required"),
    otherwise: schema => schema
  }),

  // TP-PA Test Fields - Conditionally Required (only when STD section shown AND a type is selected)
  UpdatedTpPaType: yup.string(),

  UpdatedTpPaType1: yup.string().when(['showStdTestInfo', 'UpdatedTpPaType'], {
    is: (showStdTestInfo: any, val: any) => showStdTestInfo === 'true' && val === 'Other',
    then: schema => schema.required("Specify Other Test"),
    otherwise: schema => schema
  }),

  UpdatedTpPaSpecimenCollectionDate: yup.string().when(['showStdTestInfo', 'UpdatedTpPaType'], {
    is: (showStdTestInfo: any, value: any) => showStdTestInfo === 'true' && value && value !== "0" && value !== "",
    then: schema => schema
      .required("Specimen Collection date is required")
      .test('date-validation', 'TP-PA Date is not valid',
        value => !value || (new Date(value) <= today && new Date(value) >= minDate)),
    otherwise: schema => schema
  }),

  UpdatedTpPaDetails: yup.string().when(['showStdTestInfo', 'UpdatedTpPaType'], {
    is: (showStdTestInfo: any, value: any) => showStdTestInfo === 'true' && value && value !== "0" && value !== "",
    then: schema => schema.required("Detail is required").notOneOf([0, "0"] as any, "Detail is required"),
    otherwise: schema => schema
  }),

  // FTA-ABS Test Fields - Conditionally Required (only when STD section shown AND a type is selected)
  UpdatedFtaAbsType: yup.string(),

  UpdatedFtaAbsType1: yup.string().when(['showStdTestInfo', 'UpdatedFtaAbsType'], {
    is: (showStdTestInfo: any, val: any) => showStdTestInfo === 'true' && val === 'Other',
    then: schema => schema.required("Specify Other Test"),
    otherwise: schema => schema
  }),

  UpdatedFtaAbsSpecimenCollectionDate: yup.string().when(['showStdTestInfo', 'UpdatedFtaAbsType'], {
    is: (showStdTestInfo: any, value: any) => showStdTestInfo === 'true' && value && value !== "0" && value !== "",
    then: schema => schema
      .required("Specimen Collection date is required")
      .test('date-validation', 'FTA-ABS Date is not valid',
        value => !value || (new Date(value) <= today && new Date(value) >= minDate)),
    otherwise: schema => schema
  }),

  UpdatedFtaAbsDetails: yup.string().when(['showStdTestInfo', 'UpdatedFtaAbsType'], {
    is: (showStdTestInfo: any, value: any) => showStdTestInfo === 'true' && value && value !== "0" && value !== "",
    then: schema => schema.required("Detail is required").notOneOf([0, "0"] as any, "Detail is required"),
    otherwise: schema => schema
  }),

  // EIA Test Fields - Conditionally Required (only when STD section shown AND a type is selected)
UpdatedEiaType: yup.string(),

UpdatedEiaType1: yup.string().when(['showStdTestInfo', 'UpdatedEiaType'], {
  is: (showStdTestInfo: any, val: any) => showStdTestInfo === 'true' && val === 'Other',
  then: schema => schema.required("Specify Other Test"),
  otherwise: schema => schema
}),

UpdatedEiaDate: yup.string().when
(['showStdTestInfo', 'UpdatedEiaType'], {
  is: (showStdTestInfo: any, value: any) =>
    showStdTestInfo === 'true' && value && value !== "0" && value !== "",
  then: schema =>
    schema
      .required("Specimen Collection date is required")
      .test(
        'date-validation',
        'EIA Date is not valid',
        value =>
          !value ||
          (new Date(value) <= today && new Date(value) >= minDate)
      ),
  otherwise: schema => schema
}),

UpdatedEiaSpecimenId: yup.string().nullable(),

UpdatedEiaDetails: yup.string().when(['showStdTestInfo', 'UpdatedEiaType'], {
  is: (showStdTestInfo: any, value: any) =>
    showStdTestInfo === 'true' && value && value !== "0" && value !== "",
  then: schema =>
    schema
      .required("Detail is required")
      .notOneOf([0, "0"] as any, "Detail is required"),
  otherwise: schema => schema
}),


  // Chlamydia Test Fields - Conditionally Required (only when STD section shown AND a type is selected)
  UpdatedChlamydiaType: yup.string(),

  UpdatedChlamydiaType1: yup.string().when(['showStdTestInfo', 'UpdatedChlamydiaType'], {
    is: (showStdTestInfo: any, val: any) => showStdTestInfo === 'true' && val === 'Other',
    then: schema => schema.required("Specify Other Test"),
    otherwise: schema => schema
  }),

  UpdatedChlamydiaDate: yup.string().when(['showStdTestInfo', 'UpdatedChlamydiaType'], {
    is: (showStdTestInfo: any, value: any) => showStdTestInfo === 'true' && value && value !== "0" && value !== "",
    then: schema => schema
      .required("Specimen Collection date is required")
      .test('date-validation', 'Chlamydia Date is not valid',
        value => !value || (new Date(value) <= today && new Date(value) >= minDate)),
    otherwise: schema => schema
  }),

  UpdatedChlamydiaDetails: yup.string().when(['showStdTestInfo', 'UpdatedChlamydiaType'], {
    is: (showStdTestInfo: any, value: any) => showStdTestInfo === 'true' && value && value !== "0" && value !== "",
    then: schema => schema.required("Detail is required").notOneOf([0, "0"] as any, "Detail is required"),
    otherwise: schema => schema
  }),

  // Gonorrhea Test Fields - Conditionally Required (only when STD section shown AND a type is selected)
  UpdatedGonorrheaType: yup.string(),

  UpdatedGonorrheaType1: yup.string().when(['showStdTestInfo', 'UpdatedGonorrheaType'], {
    is: (showStdTestInfo: any, val: any) => showStdTestInfo === 'true' && val === 'Other',
    then: schema => schema.required("Specify Other Test"),
    otherwise: schema => schema
  }),

  UpdatedGonorrheaSpecimenCollectionDate: yup.string().when(['showStdTestInfo', 'UpdatedGonorrheaType'], {
    is: (showStdTestInfo: any, value: any) => showStdTestInfo === 'true' && value && value !== "0" && value !== "",
    then: schema => schema
      .required("Specimen Collection date is required")
      .test('date-validation', 'Gonorrhea Date is not valid',
        value => !value || (new Date(value) <= today && new Date(value) >= minDate)),
    otherwise: schema => schema
  }),

  UpdatedGonorrheaDetails: yup.string().when(['showStdTestInfo', 'UpdatedGonorrheaType'], {
    is: (showStdTestInfo: any, value: any) => showStdTestInfo === 'true' && value && value !== "0" && value !== "",
    then: schema => schema
      .required("Details are required").notOneOf([0, "0"] as any, "Details are required"),
    otherwise: schema => schema
  }),

  pregnantId: yup
    .string()
    .nullable()
    .when(["CaseBirthGender"], {
      is: (val: any) => val?.toLowerCase() == "female",
      then: (schema) =>
        schema
          .required("Select an option")
          .typeError("Select an option"),
    }),

  dateLmp: yup
    .string()
    .nullable()
    .when(["pregnantId"], {
      is: (preg: any) => preg == 1,
      then: (schema) =>
        schema
          .required("LMP Date is Required")
          .typeError("LMP Date is Required"),
    })
    .test(
      "dob-not-in-future",
      "LMP Date is not valid",
      (value) => {
        if (!value) return true; // This field is not a required one
        const inputDate = new Date(value);
        return inputDate <= today && inputDate >= minDate;
      }
    ),

  rPRType: yup.string(),


  rpR_Result: yup.string().when(['rPRType'], {
    is: (type: any) => type && type !== '0' && type !== '',
    then: (schema) => schema.required("Detail is required"),
    otherwise: (schema) => schema.notRequired().nullable().default(undefined),
  }),

  rprCollectionDate: yup.string().when('rPRType', {
    is: (type: string) => !!type && type !== '0',
    then: (schema) =>
      schema.required('Specimen collection date is required')
        .test(
          'date-validation',
          'RPR Date is not valid',
          value => {
            if (!value) return false; // Missing date when required: show "required" error.
            const inputDate = new Date(value);
            return inputDate <= today && inputDate >= minDate; // Valid date in range.
          }
        ),
    otherwise: (schema) =>
      schema.notRequired()
        .nullable()
        .default(undefined)
        .test(
          'date-validation',
          'Specimen collection date is required',
          value => {
            if (!value) return true; // Optional field.
            const inputDate = new Date(value);
            return inputDate <= today && inputDate >= minDate; // Valid date in range.
          }
        )
  }),

  vDRLType: yup.string(),


  vdrL_Result: yup.string().when(['vDRLType'], {
    is: (type: any) => type && type !== '0' && type !== '',
    then: (schema) => schema.required("Detail is required"),
    otherwise: (schema) => schema.notRequired().nullable().default(undefined),
  }),

  vdrlCollectionDate: yup.string().when('vDRLType', {
    is: (type: string) => !!type && type !== '0',
    then: (schema) =>
      schema.required('Specimen collection date is required')
        .test(
          'date-validation',
          'VDRL Date is not valid',
          value => {
            if (!value) return false; // Missing date when required: show "required" error.
            const inputDate = new Date(value);
            return inputDate <= today && inputDate >= minDate; // Valid date in range.
          }
        ),
    otherwise: (schema) =>
      schema.notRequired()
        .nullable()
        .default(undefined)
        .test(
          'date-validation',
          'Specimen collection date is required',
          value => {
            if (!value) return true; // Optional field.
            const inputDate = new Date(value);
            return inputDate <= today && inputDate >= minDate; // Valid date in range.
          }
        )
  }),

  tPPAType: yup.string(),

  tppA_Result: yup.string().when(['tPPAType'], {
    is: (type: any) => type && type !== '0' && type !== '',
    then: (schema) => schema.required("Detail is required"),
    otherwise: (schema) => schema.notRequired().nullable().default(undefined),
  }),

  tppaCollectionDate: yup.string().when('tPPAType', {
    is: (type: string) => !!type && type !== '0',
    then: (schema) =>
      schema.required('Specimen collection date is required')
        .test(
          'date-validation',
          'TPPA Date is not valid',
          value => {
            if (!value) return false; // Missing date when required: show "required" error.
            const inputDate = new Date(value);
            return inputDate <= today && inputDate >= minDate; // Valid date in range.
          }
        ),
    otherwise: (schema) =>
      schema.notRequired()
        .nullable()
        .default(undefined)
        .test(
          'date-validation',
          'Specimen collection date is required',
          value => {
            if (!value) return true; // Optional field.
            const inputDate = new Date(value);
            return inputDate <= today && inputDate >= minDate; // Valid date in range.
          }
        )
  }),

  fTAABSType: yup.string(),

  ftaabS_Result: yup.string().when(['fTAABSType'], {
    is: (type: any) => type && type !== '0' && type !== '',
    then: (schema) => schema.required("Detail is required"),
    otherwise: (schema) => schema.notRequired().nullable().default(undefined),
  }),

  ftA_ABS_Specimen_Collection_Date: yup.string().when('fTAABSType', {
    is: (type: string) => !!type && type !== '0',
    then: (schema) =>
      schema.required('Specimen collection date is required')
        .test(
          'date-validation',
          'FTA-ABS Date is not valid',
          value => {
            if (!value) return false; // Missing date when required: show "required" error.
            const inputDate = new Date(value);
            return inputDate <= today && inputDate >= minDate; // Valid date in range.
          }
        ),
    otherwise: (schema) =>
      schema.notRequired()
        .nullable()
        .default(undefined)
        .test(
          'date-validation',
          'Specimen collection date is required',
          value => {
            if (!value) return true; // Optional field.
            const inputDate = new Date(value);
            return inputDate <= today && inputDate >= minDate; // Valid date in range.
          }
        )
  }),

eIAIgG_Type: yup.string(),

eIAIgG_Result: yup.string().when(['eIAIgG_Type'], {
  is: (type: any) => type && type !== '0' && type !== '',
  then: (schema) => schema.required("Detail is required"),
  otherwise: (schema) =>
    schema.notRequired().nullable().default(undefined),
}),

eIAIgGSpecimenCollectionDate: yup.string().when('eIAIgG_Type', {
  is: (type: string) => !!type && type !== '0',
  then: (schema) =>
    schema
      .required('Specimen collection date is required')
      .test(
        'date-validation',
        'EIA Date is not valid',
        (value) => {
          if (!value) return false;
          const inputDate = new Date(value);
          return inputDate <= today && inputDate >= minDate;
        }
      ),
  otherwise: (schema) =>
    schema
      .notRequired()
      .nullable()
      .default(undefined)
      .test(
        'date-validation',
        'Specimen collection date is required',
        (value) => {
          if (!value) return true;
          const inputDate = new Date(value);
          return inputDate <= today && inputDate >= minDate;
        }
      ),
}),

  chlamydia_Type: yup.string(),

  chlamydia_Result: yup.string().when(['chlamydia_Type'], {
    is: (type: any) => type && type !== '0' && type !== '',
    then: (schema) => schema.required("Detail is required"),
    otherwise: (schema) => schema.notRequired().nullable().default(undefined),
  }),

  chlamydiaDate: yup.string().when('chlamydia_Type', {
    is: (type: string) => !!type && type !== '0',
    then: (schema) =>
      schema.required('Specimen collection date is required')
        .test(
          'date-validation',
          'Chlamydia Date is not valid',
          value => {
            if (!value) return false; // Missing date when required: show "required" error.
            const inputDate = new Date(value);
            return inputDate <= today && inputDate >= minDate; // Valid date in range.
          }
        ),
    otherwise: (schema) =>
      schema.notRequired()
        .nullable()
        .default(undefined)
        .test(
          'date-validation',
          'Specimen collection date is required',
          value => {
            if (!value) return true; // Optional field.
            const inputDate = new Date(value);
            return inputDate <= today && inputDate >= minDate; // Valid date in range.
          }
        )
  }),

  gonorrhea_Type: yup.string(),


  gonorrhea_Result: yup.string().when(['gonorrhea_Type'], {
    is: (type: any) => type && type !== '0' && type !== '',
    then: (schema) => schema.required("Detail is required"),
    otherwise: (schema) => schema.notRequired().nullable().default(undefined),
  }),

  gonorrhea_Specimen_Collection_Date: yup.string().when('gonorrhea_Type', {
    is: (type: string) => !!type && type !== '0',
    then: (schema) =>
      schema.required('Specimen collection date is required')
        .test(
          'date-validation',
          'Gonorrhea Date is not valid',
          value => {
            if (!value) return false; // Missing date when required: show "required" error.
            const inputDate = new Date(value);
            return inputDate <= today && inputDate >= minDate; // Valid date in range.
          }
        ),
    otherwise: (schema) =>
      schema.notRequired()
        .nullable()
        .default(undefined)
        .test(
          'date-validation',
          'Specimen collection date is required',
          value => {
            if (!value) return true; // Optional field.
            const inputDate = new Date(value);
            return inputDate <= today && inputDate >= minDate; // Valid date in range.
          }
        )
  }),

  ftaabsCollectionDate: yup.string(),
  eIAIgGCollectionDate: yup.string(),
  chlamydiaCollectionDate: yup.string(),
  gonorrheaCollectionDate: yup.string(),
  
});